/**
 * Product Videos Circle Component
 * Displays video files in circles with autoplay sequence and fullscreen modal slider
 */
if (!customElements.get('boreal-product-video-stories')) {
  customElements.define('boreal-product-video-stories', class extends HTMLElement {
    #controller = new AbortController();
    #scrollTimeout = null;
    #isScrolling = false;
    
    constructor() {
      super();
      this.videos = [];
      this.modalVideos = [];
      this.singleVideos = []; // For mobile single video mode
      this.currentIndex = 0;
      this.modalCurrentIndex = 0;
      this.intervalId = null;
      this.playPromises = new Map();
      this.observer = null;
      this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    connectedCallback() {
      this.initializeVideos();
      if (!this.isMobile) {
        this.initializeModal();
      } else {
        this.initializeMobileProductCards();
      }
      this.initializeAddToCart();
    }

    disconnectedCallback() {
      if (this.intervalId) clearInterval(this.intervalId);
      if (this.observer) this.observer.disconnect();
      if (this.#scrollTimeout) clearTimeout(this.#scrollTimeout);
      this.#controller.abort();
      this.playPromises.clear();
    }

    initializeVideos() {
      const videoItems = this.querySelectorAll('[ref="videoItems"]');
      if (!videoItems.length) return;

      const { signal: videoSignal } = this.#controller;

      videoItems.forEach((item) => {
        const video = item.querySelector('video');
        if (video) {
          video.muted = true;
          video.playsInline = true;
          video.loop = false;
          video.preload = 'none';
          video.setAttribute('webkit-playsinline', 'true');
          this.videos.push(video);
        }

        // Disable hover effects on mobile
        if (!this.isMobile) {
          item.addEventListener('mouseenter', () => this.handleHover(item, true), { passive: true, signal: videoSignal });
          item.addEventListener('mouseleave', () => this.handleHover(item, false), { passive: true, signal: videoSignal });
        }
        
        item.addEventListener('click', () => {
          const index = parseInt(item.dataset.index, 10);
          if (this.isMobile) {
            this.openNativeVideoOnMobile(video, index);
          } else {
            this.openModal(index);
          }
        }, { signal: videoSignal });
      });

      // Only autoplay circles on desktop
      if (this.videos.length && !this.isMobile) this.startSequence();
    }

    openNativeVideoOnMobile(video, index) {
      const fullscreenContainer = this.querySelector('[ref="mobileFullscreen"]');
      const fullscreenSlider = this.querySelector('[ref="fullscreenSlider"]');
      const closeButton = this.querySelector('[ref="fullscreenClose"]');
      const prevButton = this.querySelector('[ref="fullscreenPrev"]');
      const nextButton = this.querySelector('[ref="fullscreenNext"]');
      
      if (!fullscreenContainer || !fullscreenSlider) return;
      
      this.currentFullscreenIndex = index;
      const slides = fullscreenSlider.querySelectorAll('.boreal-product-video-stories__fullscreen-slide');
      this.fullscreenSlideCount = slides.length;
      
      // Show fullscreen
      fullscreenContainer.style.display = 'block';
      document.body.style.overflow = 'hidden';
      
      // Scroll to the correct slide
      const targetSlide = slides[index];
      
      if (targetSlide) {
        targetSlide.scrollIntoView({ behavior: 'instant', inline: 'start' });
        
        // Get and play the video in this slide
        const videoElement = targetSlide.querySelector('video');
        if (videoElement) {
          videoElement.load();
          videoElement.addEventListener('canplay', () => {
            videoElement.play().catch(e => console.log('Autoplay failed:', e));
            // Hide controls initially
            setTimeout(() => {
              videoElement.removeAttribute('controls');
            }, 100);
          }, { once: true });
          
          // Show controls on tap
          videoElement.addEventListener('click', () => {
            videoElement.setAttribute('controls', 'true');
            setTimeout(() => {
              videoElement.removeAttribute('controls');
            }, 3000);
          });
        }
      }
      
      // Handle scroll to load videos on demand
      const handleScroll = () => {
        const scrollLeft = fullscreenSlider.scrollLeft;
        const slideWidth = slides[0]?.offsetWidth || window.innerWidth;
        const currentIndex = Math.round(scrollLeft / slideWidth);
        
        this.currentFullscreenIndex = currentIndex;
        
        // Pause all videos except current
        slides.forEach((slide, i) => {
          const vid = slide.querySelector('video');
          if (vid) {
            if (i === currentIndex) {
              if (vid.paused) {
                vid.load();
                vid.play().catch(() => {});
              }
            } else {
              vid.pause();
            }
          }
        });
      };
      
      // Navigate to previous slide
      const handlePrev = () => {
        const newIndex = this.currentFullscreenIndex > 0 
          ? this.currentFullscreenIndex - 1 
          : this.fullscreenSlideCount - 1;
        
        const targetSlide = slides[newIndex];
        if (targetSlide) {
          targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
        }
      };
      
      // Navigate to next slide
      const handleNext = () => {
        const newIndex = this.currentFullscreenIndex < this.fullscreenSlideCount - 1 
          ? this.currentFullscreenIndex + 1 
          : 0;
        
        const targetSlide = slides[newIndex];
        if (targetSlide) {
          targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
        }
      };
      
      fullscreenSlider.addEventListener('scroll', handleScroll, { passive: true });
      prevButton?.addEventListener('click', handlePrev);
      nextButton?.addEventListener('click', handleNext);
      
      // Close handler
      const closeFullscreen = () => {
        fullscreenContainer.style.display = 'none';
        document.body.style.overflow = '';
        
        // Pause all videos
        slides.forEach(slide => {
          const vid = slide.querySelector('video');
          if (vid) vid.pause();
        });
        
        fullscreenSlider.removeEventListener('scroll', handleScroll);
        prevButton?.removeEventListener('click', handlePrev);
        nextButton?.removeEventListener('click', handleNext);
        closeButton.removeEventListener('click', closeFullscreen);
      };
      
      closeButton.addEventListener('click', closeFullscreen, { once: true });
      
      // Update the visible product card below circles
      this.showMobileProductCard(index);
    }

    initializeMobileProductCards() {
      // Show first product card by default if exists
      const mobileProducts = this.querySelector('[ref="mobileProducts"]');
      if (mobileProducts) {
        const firstCard = mobileProducts.querySelector('[data-product-index="0"]');
        if (firstCard) {
          firstCard.style.display = 'flex';
        }
      }
    }

    showMobileProductCard(index) {
      const mobileProducts = this.querySelector('[ref="mobileProducts"]');
      if (!mobileProducts) return;
      
      const allCards = mobileProducts.querySelectorAll('.boreal-product-video-stories__mobile-product-card');
      allCards.forEach(card => card.style.display = 'none');
      
      const currentCard = mobileProducts.querySelector(`[data-product-index="${index}"]`);
      if (currentCard) {
        currentCard.style.display = 'flex';
      }
    }

    async handleHover(item, isEnter) {
      const video = item.querySelector('video');
      if (!video) return;

      if (isEnter) {
        if (this.intervalId) clearInterval(this.intervalId);
        this.videos.forEach(v => { if (v !== video) v.pause(); });
        video.currentTime = 0;
        try {
          await video.play();
        } catch (e) {
          // Autoplay blocked
        }
      } else {
        video.pause();
        video.currentTime = 0;
        this.startSequence();
      }
    }

    initializeModal() {
      const modal = this.querySelector('[ref="videoModal"]');
      const closeButton = this.querySelector('[ref="closeModal"]');
      const prevButton = this.querySelector('[ref="prevButton"]');
      const nextButton = this.querySelector('[ref="nextButton"]');

      if (!modal) return;

      const { signal: modalSignal } = this.#controller;

      closeButton?.addEventListener('click', () => this.closeModal(), { signal: modalSignal });
      modal.addEventListener('click', (e) => {
        if (e.target === modal) this.closeModal();
      }, { signal: modalSignal });
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeModal();
        if (e.key === 'ArrowLeft') this.previousSlide();
        if (e.key === 'ArrowRight') this.nextSlide();
      }, { signal: modalSignal });

      prevButton?.addEventListener('click', () => this.previousSlide(), { signal: modalSignal });
      nextButton?.addEventListener('click', () => this.nextSlide(), { signal: modalSignal });

      if (this.isMobile) {
        // Mobile: simple single video mode
        this.initializeMobileModal();
      } else {
        // Desktop: slider mode
        this.initializeDesktopModal();
      }
    }

    initializeMobileModal() {
      const singleContainer = this.querySelector('[ref="modalSingle"]');
      if (!singleContainer) return;

      const singleSlides = singleContainer.querySelectorAll('.boreal-product-video-stories__single-slide');
      this.singleVideos = Array.from(singleContainer.querySelectorAll('.boreal-product-video-stories__single-video'));
      
      this.singleVideos.forEach(video => {
        video.preload = 'none';
        video.playsInline = true;
        video.setAttribute('webkit-playsinline', 'true');
      });
    }

    initializeDesktopModal() {
      const slider = this.querySelector('[ref="modalSlider"]');
      if (!slider) return;

      const slides = slider.querySelectorAll('.boreal-product-video-stories__modal-slide');
      this.modalVideos = Array.from(slider.querySelectorAll('.boreal-product-video-stories__modal-video'));
      
      // Configure modal videos - don't preload on mobile
      this.modalVideos.forEach(video => {
        video.preload = 'none';
        video.playsInline = true;
        video.setAttribute('webkit-playsinline', 'true');
      });

      // Disable IntersectionObserver on mobile - use manual navigation only
      if (!this.isMobile) {
        this.observer = new IntersectionObserver((entries) => {
          if (this.#isScrolling) return;
          
          entries.forEach(async entry => {
            if (entry.isIntersecting) {
              slides.forEach(s => s.classList.remove('is-active'));
              entry.target.classList.add('is-active');
              
              const index = parseInt(entry.target.dataset.slideIndex, 10);
              this.modalCurrentIndex = index;

              for (const video of this.modalVideos) {
                const playPromise = this.playPromises.get(video);
                if (playPromise) await playPromise.catch(() => {});
                video.pause();
                this.playPromises.delete(video);
              }

              const videoToPlay = this.modalVideos[index];
              if (videoToPlay) {
                try {
                  const playPromise = videoToPlay.play();
                  this.playPromises.set(videoToPlay, playPromise);
                  await playPromise;
                } catch (e) {
                  // Autoplay blocked
                }
              }
            }
          });
        }, { root: slider, threshold: 0.5, rootMargin: '-40% 0px' });

        const { signal: slideSignal } = this.#controller;

        slides.forEach(slide => {
          if (this.observer) this.observer.observe(slide);
          slide.addEventListener('click', () => {
            const index = parseInt(slide.dataset.slideIndex, 10);
            if (index !== this.modalCurrentIndex) this.scrollToSlide(index);
          }, { signal: slideSignal });
        });

        slider.addEventListener('scroll', () => {
          this.#isScrolling = true;
          if (this.#scrollTimeout) clearTimeout(this.#scrollTimeout);
          
          this.#scrollTimeout = setTimeout(() => {
            const scrollLeft = slider.scrollLeft;
            const slideWidth = slides[0]?.offsetWidth || 0;
            const centerIndex = Math.round(scrollLeft / slideWidth);
            
            if (centerIndex !== this.modalCurrentIndex && centerIndex >= 0 && centerIndex < slides.length) {
              this.handleSlideChange(centerIndex);
            }
            this.#scrollTimeout = null;
            this.#isScrolling = false;
          }, 100);
        }, { passive: true, signal: slideSignal });
      }
    }

    async handleSlideChange(index) {
      const slider = this.querySelector('[ref="modalSlider"]');
      const slides = slider?.querySelectorAll('.boreal-product-video-stories__modal-slide');
      
      if (!slides || index < 0 || index >= slides.length) return;

      slides.forEach(s => s.classList.remove('is-active'));
      slides[index].classList.add('is-active');
      this.modalCurrentIndex = index;

      const videoToPlay = this.modalVideos[index];
      
      // Pause other videos
      for (let i = 0; i < this.modalVideos.length; i++) {
        if (i !== index) {
          const video = this.modalVideos[i];
          const playPromise = this.playPromises.get(video);
          if (playPromise) await playPromise.catch(() => {});
          video.pause();
          this.playPromises.delete(video);
        }
      }

      if (videoToPlay) {
        videoToPlay.currentTime = 0;
        
        // Load video on demand before playing
        if (videoToPlay.readyState < 2) {
          videoToPlay.load();
        }
        
        try {
          const playPromise = videoToPlay.play();
          this.playPromises.set(videoToPlay, playPromise);
          await playPromise;
        } catch (e) {
          // Autoplay blocked
        }
      }
    }

    openModal(index) {
      const modal = this.querySelector('[ref="videoModal"]');
      if (!modal) return;

      if (this.intervalId) clearInterval(this.intervalId);
      this.videos.forEach(v => v.pause());
      this.modalCurrentIndex = index;
      modal.showModal?.();

      if (this.isMobile) {
        this.showMobileVideo(index);
      } else {
        this.showDesktopVideo(index);
      }
    }

    showMobileVideo(index) {
      const singleContainer = this.querySelector('[ref="modalSingle"]');
      if (!singleContainer) return;

      const singleSlides = singleContainer.querySelectorAll('.boreal-product-video-stories__single-slide');
      
      // Hide all slides
      singleSlides.forEach(slide => slide.style.display = 'none');
      
      // Show current slide
      const currentSlide = singleSlides[index];
      if (currentSlide) {
        currentSlide.style.display = 'flex';
        
        const currentVideo = this.singleVideos[index];
        if (currentVideo) {
          currentVideo.currentTime = 0;
          currentVideo.load();
          
          currentVideo.addEventListener('canplay', () => {
            const playPromise = currentVideo.play();
            if (playPromise !== undefined) {
              this.playPromises.set(currentVideo, playPromise);
              playPromise
                .then(() => this.playPromises.delete(currentVideo))
                .catch(() => this.playPromises.delete(currentVideo));
            }
          }, { once: true });
        }
      }
    }

    showDesktopVideo(index) {
      const slider = this.querySelector('[ref="modalSlider"]');
      if (!slider) return;

      requestAnimationFrame(() => {
        const slides = slider.querySelectorAll('.boreal-product-video-stories__modal-slide');
        
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
        });

        const targetSlide = slides[index];
        if (targetSlide) {
          targetSlide.scrollIntoView({ behavior: 'instant', inline: 'center', block: 'nearest' });
        }

        const currentVideo = this.modalVideos[index];
        if (currentVideo) {
          currentVideo.currentTime = 0;
          
          // Load video on demand, then play
          currentVideo.load();
          currentVideo.addEventListener('canplay', () => {
            const playPromise = currentVideo.play();
            if (playPromise !== undefined) {
              this.playPromises.set(currentVideo, playPromise);
              playPromise
                .then(() => this.playPromises.delete(currentVideo))
                .catch(() => this.playPromises.delete(currentVideo));
            }
          }, { once: true });
        }
      });
    }

    async closeModal() {
      const modal = this.querySelector('[ref="videoModal"]');
      if (!modal) return;

      // Stop all videos
      const allVideos = this.isMobile ? this.singleVideos : this.modalVideos;
      for (const video of allVideos) {
        const playPromise = this.playPromises.get(video);
        if (playPromise) await playPromise.catch(() => {});
        video.pause();
        video.currentTime = 0;
        this.playPromises.delete(video);
      }

      modal.close?.();
      if (this.videos.length && !this.isMobile) this.startSequence();
    }

    async scrollToSlide(index) {
      const slider = this.querySelector('[ref="modalSlider"]');
      if (!slider) return;

      const slides = slider.querySelectorAll('.boreal-product-video-stories__modal-slide');
      const targetSlide = slides[index];
      
      if (targetSlide) {
        slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
        targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        for (const video of this.modalVideos) {
          const playPromise = this.playPromises.get(video);
          if (playPromise) await playPromise.catch(() => {});
          video.pause();
          this.playPromises.delete(video);
        }
        
        const targetVideo = targetSlide.querySelector('.boreal-product-video-stories__modal-video');
        if (targetVideo) {
          try {
            const playPromise = targetVideo.play();
            this.playPromises.set(targetVideo, playPromise);
            await playPromise;
          } catch (e) {
            // Autoplay blocked
          }
        }
      }

      this.modalCurrentIndex = index;
    }

    previousSlide() {
      const videoCount = this.isMobile ? this.singleVideos.length : this.modalVideos.length;
      const newIndex = this.modalCurrentIndex > 0 
        ? this.modalCurrentIndex - 1 
        : videoCount - 1;
      
      if (this.isMobile) {
        this.changeMobileVideo(newIndex);
      } else {
        this.scrollToSlide(newIndex);
      }
    }

    nextSlide() {
      const videoCount = this.isMobile ? this.singleVideos.length : this.modalVideos.length;
      const newIndex = this.modalCurrentIndex < videoCount - 1 
        ? this.modalCurrentIndex + 1 
        : 0;
      
      if (this.isMobile) {
        this.changeMobileVideo(newIndex);
      } else {
        this.scrollToSlide(newIndex);
      }
    }

    async changeMobileVideo(newIndex) {
      const singleContainer = this.querySelector('[ref="modalSingle"]');
      if (!singleContainer) return;

      const singleSlides = singleContainer.querySelectorAll('.boreal-product-video-stories__single-slide');
      
      // Stop current video
      const currentVideo = this.singleVideos[this.modalCurrentIndex];
      if (currentVideo) {
        const playPromise = this.playPromises.get(currentVideo);
        if (playPromise) await playPromise.catch(() => {});
        currentVideo.pause();
        this.playPromises.delete(currentVideo);
      }

      // Hide all slides
      singleSlides.forEach(slide => slide.style.display = 'none');
      
      // Show new slide
      const newSlide = singleSlides[newIndex];
      if (newSlide) {
        newSlide.style.display = 'flex';
        this.modalCurrentIndex = newIndex;
        
        const newVideo = this.singleVideos[newIndex];
        if (newVideo) {
          newVideo.currentTime = 0;
          newVideo.load();
          
          newVideo.addEventListener('canplay', () => {
            const playPromise = newVideo.play();
            if (playPromise !== undefined) {
              this.playPromises.set(newVideo, playPromise);
              playPromise
                .then(() => this.playPromises.delete(newVideo))
                .catch(() => this.playPromises.delete(newVideo));
            }
          }, { once: true });
        }
      }
    }

    startSequence() {
      this.playVideo(this.currentIndex);
      this.intervalId = setInterval(() => this.nextVideo(), 3000);
    }

    nextVideo() {
      this.currentIndex = (this.currentIndex + 1) % this.videos.length;
      this.playVideo(this.currentIndex);
    }

    async playVideo(index) {
      if (index < 0 || index >= this.videos.length) return;

      for (const v of this.videos) {
        const promise = this.playPromises.get(v);
        if (promise) await promise.catch(() => {});
        v.pause();
        this.playPromises.delete(v);
      }

      const video = this.videos[index];
      video.currentTime = 0;

      try {
        const playPromise = video.play();
        this.playPromises.set(video, playPromise);
        await playPromise;
        this.playPromises.delete(video);
      } catch (e) {
        this.playPromises.delete(video);
      }
    }

    initializeAddToCart() {
      // Use event delegation to handle dynamically loaded forms
      this.addEventListener('submit', async (e) => {
        // Check if the submitted element is our add to cart form
        if (!e.target.classList.contains('boreal-product-video-stories__add-to-cart-form')) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        const form = e.target;
        const formData = new FormData(form);
        const button = form.querySelector('button[type="submit"]');
        const originalText = button.textContent;
        
        button.disabled = true;
        button.textContent = 'Ajout...';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            const data = await response.json();
            
            // Sur mobile, fermer le fullscreen au lieu du modal
            if (this.isMobile) {
              const fullscreenContainer = this.querySelector('[ref="mobileFullscreen"]');
              if (fullscreenContainer && fullscreenContainer.style.display === 'block') {
                fullscreenContainer.style.display = 'none';
                document.body.style.overflow = '';
                const fullscreenSlider = this.querySelector('[ref="fullscreenSlider"]');
                if (fullscreenSlider) {
                  const videos = fullscreenSlider.querySelectorAll('video');
                  videos.forEach(video => video.pause());
                }
              }
            } else {
              // Desktop: arrêter toutes les vidéos et fermer le modal
              for (const video of this.modalVideos) {
                const playPromise = this.playPromises.get(video);
                if (playPromise) await playPromise.catch(() => {});
                video.pause();
                this.playPromises.delete(video);
              }
              
              const modal = this.querySelector('[ref="videoModal"]');
              if (modal) {
                modal.close?.();
              }
              
              // Redémarrer l'autoplay des cercles
              if (this.videos.length) this.startSequence();
            }
            
            // Déclencher l'événement CartAddEvent pour mettre à jour le cart
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: data,
                data: {
                  source: 'boreal-product-video-stories',
                  variantId: formData.get('id'),
                  itemCount: data.item_count
                }
              }
            }));
            
            // Ouvrir le cart drawer
            setTimeout(() => {
              const cartDrawer = document.querySelector('cart-drawer-component');
              if (cartDrawer && typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }
            }, 100);
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
        } finally {
          button.disabled = false;
          button.textContent = originalText;
        }
      });
    }
  });
}
